import React from 'react';
import { buildClassName } from '../../utils';
import { useDesktopNavHover } from '../../hooks/useDesktopNavHover';
import { NavigationDivider } from './internal/NavigationDivider';
import { NavigationButtonCustom } from './internal/NavigationButtonCustom';
import { NavigationButtonDropdown } from './internal/NavigationButtonDropdown';
import { NavigationButtonLink } from './internal/NavigationButtonLink';
import type { NavigationButton } from '../../types';
import type { DesktopNavigationProps } from './types';
import styles from './DesktopNavigation.module.scss';

export const DesktopNavigation = ({ 
  items, 
  behavior = { 
    dropdownTrigger: 'hover', 
    closeOnOutsideClick: true,
    enableSubmenus: true,
    mobileExpandInline: false,
  }, 
  expanded = false,
  onAction,
  showChevron = true,
  styleOverrides = {},
  isCrawler = false,
}: DesktopNavigationProps) => {
  const isHoverMode = behavior?.dropdownTrigger === 'hover';
  const { handleItemMouseEnter, handleItemClick, handleNavLeave, isItemExpanded, navRef } = useDesktopNavHover({
    isHoverMode,
    expanded,
    isCrawler,
    closeOnOutsideClick: behavior.closeOnOutsideClick,
  });

  const renderNavigationItem = (item: NavigationButton) => {
    const isExpanded = isItemExpanded(item.id);
    const onMouseEnter = () => handleItemMouseEnter(item.id);
    const onClick = () => handleItemClick(item.id);

    switch (item.buttonVariant) {
      case 'divider':
        return <NavigationDivider className={styleOverrides.divider} />;
        
      case 'custom':
        return item.customContent ? (
          <NavigationButtonCustom
            item={item}
            isExpanded={isExpanded}
            onMouseEnter={onMouseEnter}
            onClick={onClick}
            showChevron={showChevron}
            styleOverrides={styleOverrides}
          />
        ) : null;
        
      case 'dropdown':
        return item.items ? (
          <NavigationButtonDropdown
            item={item}
            isExpanded={isExpanded}
            isCrawler={isCrawler}
            onMouseEnter={onMouseEnter}
            onClick={onClick}
            onAction={onAction}
            showChevron={showChevron}
            styleOverrides={styleOverrides}
          />
        ) : null;
        
      case 'link':
        return (
          <NavigationButtonLink
            item={item}
            onAction={onAction}
            styleOverrides={styleOverrides}
          />
        );
        
      default:
        return null;
    }
  };

  return (
    <nav 
      ref={navRef}
      className={buildClassName(styles.navigation, styleOverrides.navigation)}
      onMouseLeave={handleNavLeave}
    >
      {items.map((item, index) => {
        return <React.Fragment key={item.id || `item-${index}`}>{renderNavigationItem(item)}</React.Fragment>;
      })}
    </nav>
  );
}; 